import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js';

// Global state to persist filter selection across card switches
// Persist selection across tabs using localStorage; default to 'all' for stability with sparse data
let globalActiveRange = (typeof window !== 'undefined' && window.localStorage)
  ? (window.localStorage.getItem('chart.activeRange') || 'all')
  : 'all';
let globalDateRange = { start: null, end: null };
// Global state to persist dataset visibility
let globalDatasetVisibility = {
  0: true,  // Beginning Balance
  1: true,  // Transactions
  2: true,  // Interest
  3: true,  // Payments
  4: true,  // Balance Subject to IR
  5: true   // End Balance
};
// Global state for netting functionality
let globalNetEnabled = (typeof window !== 'undefined' && window.localStorage)
  ? (window.localStorage.getItem('chart.netEnabled') === 'true')
  : false;
// Global state for cumulative functionality
let globalCumulativeEnabled = (typeof window !== 'undefined' && window.localStorage)
  ? (window.localStorage.getItem('chart.cumulativeEnabled') === 'true')
  : false;

function GroupedBarChart({ data, selectedStatementId }) {
  const canvasRef = useRef(null); // Canvas ref for Chart.js
  const chartRef = useRef(null);   // Store the chart instance

  // State for date range filter - initialize with global state
  const [dateRange, setDateRange] = useState(globalDateRange);
  const [filteredData, setFilteredData] = useState([]); // data after filtering
  const [activeRange, setActiveRange] = useState(globalActiveRange); // keep track of active button
  // State for netting functionality
  const [netEnabled, setNetEnabled] = useState(globalNetEnabled);
  // State for cumulative functionality
  const [cumulativeEnabled, setCumulativeEnabled] = useState(globalCumulativeEnabled);
  // State to trigger re-renders when globalDatasetVisibility changes
  const [visibilityCounter, setVisibilityCounter] = useState(0);

  // Helper function to parse the item's date, handling both aggregated and individual card data formats
  const parseItemDate = (item) => {
    if (item.period) {
      // For aggregated data, parse the period label
      // Format is typically "MMM YYYY" (e.g., "Jan 2024")
      const d = new Date(item.period);
      // If we are in Dec 2099 territory, let's log it
      if (d.getFullYear() > 2030) {
        console.warn('Extremely future date detected in chart data:', item.period, item);
      }
      return d;
    } else if (item.billing_cycle_end_date) {
      // For individual card data, parse the billing_cycle_end_date directly
      return new Date(item.billing_cycle_end_date);
    }
    return new Date(); // fallback
  };

  // Helper to filter out extreme future dates from range calculations
  const getFilteredDataForRange = (dataList) => {
    const now = new Date();
    const tenYearsFromNow = new Date(now.getFullYear() + 10, 11, 31);
    return dataList.filter(item => parseItemDate(item) <= tenYearsFromNow);
  };

  // Helper function to check if netting is applicable (works for both aggregated and individual card data)
  const isNetApplicable = () => {
    // Always allow netting when we have data - works for both individual cards and aggregated data
    return data && data.length > 0;
  };

  // Debug logging to understand data structure
  useEffect(() => {
    if (data && data.length > 0) {
      console.log('GroupedBarChart received data:', data);
      console.log('First data item:', data[0]);
      console.log('Data item keys:', Object.keys(data[0]));
    }
  }, [data]);

  // Initialize or apply current filter when data changes
  useEffect(() => {
    if (!data || data.length === 0) return;
    
    // If a specific statement is selected, override the range to show all data (which will be filtered by itemDate)
    // or specifically set the range to that month.
    if (selectedStatementId) {
        setActiveRange('selected');
        // The individual filtering in the next useEffect will handle the narrow view if we set the range correctly
        // But for now, let's just let applyCurrentFilter handle the standard ranges, 
        // and we'll add logic to detect selectedStatementId in applyCurrentFilter or similar.
    }

    applyCurrentFilter();
  }, [data, selectedStatementId]);

  // Apply the current filter to the data
  const applyCurrentFilter = () => {
    if (!data || data.length === 0) return;

    if (selectedStatementId && activeRange === 'selected') {
        // If a specific statement is selected and we are in 'selected' mode,
        // we show only that statement's month.
        // For individual cards, item.id is the integer PK. 
        // For aggregated, it's 'YYYY-MM'.
        const selectedItem = data.find(item => String(item.id) === String(selectedStatementId));
        if (selectedItem) {
            const itemDate = parseItemDate(selectedItem);
            const startDate = new Date(itemDate.getFullYear(), itemDate.getMonth(), 1);
            const endDate = new Date(itemDate.getFullYear(), itemDate.getMonth() + 1, 0);
            const newDateRange = { start: startDate, end: endDate };
            setDateRange(newDateRange);
            // Don't update globalDateRange as this is a transient selection
            return;
        }
    }

    if (activeRange === 'all' || activeRange === 'selected') {
      setDateRange({ start: null, end: null });
      globalDateRange = { start: null, end: null };
    } else {
      // Calculate date range based on current activeRange
      // Filter out extreme future dates to avoid skewing "latest" relative ranges
      const validData = getFilteredDataForRange(data);
      if (validData.length === 0) return;

      const sortedData = [...validData].sort((a, b) => {
        return parseItemDate(a) - parseItemDate(b);
      });
      
      const latestDataPoint = sortedData[sortedData.length - 1];
      let latestDate = parseItemDate(latestDataPoint);
      
      // Cap latestDate at today if it's in the future
      const now = new Date();
      if (latestDate > now) {
        latestDate = now;
      }

      let startDate;

      if (activeRange === 'YTD') {
        startDate = new Date(now.getFullYear(), 0, 1);
      } else if (activeRange === '12months') {
        startDate = new Date(latestDate);
        startDate.setMonth(startDate.getMonth() - 11); // Go back 11 months to get 12 total (current + 11 previous)
        startDate.setDate(1); // Start from beginning of that month
      } else if (activeRange === '3months') {
        startDate = new Date(latestDate);
        startDate.setMonth(startDate.getMonth() - 2); // Go back 2 months to get 3 total (current + 2 previous)
        startDate.setDate(1); // Start from beginning of that month
      }

      // End of range should be the last day of the current year for YTD per requirements
      const ytdEnd = new Date(now.getFullYear(), 11, 31);
      const endDate = (activeRange === 'YTD') ? ytdEnd : latestDate;
      const newDateRange = { start: startDate, end: endDate };
      setDateRange(newDateRange);
      globalDateRange = newDateRange;
    }
  };

  // Filter data when dateRange changes
  useEffect(() => {
    if (!data || data.length === 0) return;

    if (dateRange.start === null && dateRange.end === null) {
      // No filtering, show all data
      setFilteredData(data);
      return;
    }

    const filtered = data.filter(item => {
      const itemDate = parseItemDate(item);
      
      return (
        (!dateRange.start || itemDate >= dateRange.start) &&
        (!dateRange.end || itemDate <= dateRange.end)
      );
    });
    
    setFilteredData(filtered);
  }, [data, dateRange]);

  // Build the chart whenever filteredData updates
  useEffect(() => {
    if (!filteredData || filteredData.length === 0) return;
    
    // Add a small delay to ensure canvas is ready
    const buildChart = () => {
      if (!canvasRef.current) {
        console.log('Canvas not ready, retrying...');
        setTimeout(buildChart, 100);
        return;
      }

      console.log('Building chart with netEnabled:', netEnabled, 'cumulativeEnabled:', cumulativeEnabled);
      console.log('Filtered data length:', filteredData.length);

      // Sort filtered data chronologically
      const sortedData = [...filteredData].sort((a, b) => {
        return parseItemDate(a) - parseItemDate(b);
      });

      // Generate labels - handle both data formats
      const labels = sortedData.map(item => {
        if (item.period) {
          // For aggregated data, use the period label directly
          return item.period;
        } else if (item.billing_cycle_end_date) {
          // For individual card data, format the billing_cycle_end_date
          const date = new Date(item.billing_cycle_end_date);
          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        }
        return 'Unknown';
      });

      // Extract data with proper fallbacks for missing fields - handle both aggregated and individual card data
      const extractMoneyValue = (item, fieldName) => {
        // For individual card data, try the direct field first, then the amount property
        if (item[fieldName] !== undefined) {
          // If it's a number, return it directly (aggregated data)
          if (typeof item[fieldName] === 'number') {
            return item[fieldName];
          }
          // If it's an object with amount property (Django Money field), extract the amount
          if (item[fieldName] && typeof item[fieldName] === 'object' && 'amount' in item[fieldName]) {
            return Number(item[fieldName].amount) || 0;
          }
          // If it's a string that can be parsed as number
          if (typeof item[fieldName] === 'string') {
            return Number(item[fieldName]) || 0;
          }
        }
        return 0;
      };

      const beg_balance = sortedData.map(item => extractMoneyValue(item, 'previous_balance'));
      const transactions = sortedData.map(item => extractMoneyValue(item, 'transactions'));
      const interest = sortedData.map(item => extractMoneyValue(item, 'interest_charged'));
      const payments = sortedData.map(item => {
        const p = extractMoneyValue(item, 'payments');
        const oc = extractMoneyValue(item, 'other_credits');
        return p + oc;
      });
      const balance_int = sortedData.map(item => extractMoneyValue(item, 'balance_subject_to_interest_rate'));
      const end_balance = sortedData.map(item => extractMoneyValue(item, 'new_balance'));

      // Apply cumulative calculation if enabled
      const applyCumulative = (dataArray) => {
        if (!cumulativeEnabled) return dataArray;
        
        let cumulative = 0;
        return dataArray.map((value, index) => {
          cumulative += value;
          console.log(`Cumulative calculation for index ${index}: previous cumulative ${cumulative - value} + current value ${value} = new cumulative ${cumulative}`);
          return cumulative;
        });
      };

      // Only apply cumulative to flow-based metrics, not balance/stock metrics
      const transactionsCumulative = applyCumulative(transactions);
      const interestCumulative = applyCumulative(interest);
      const paymentsCumulative = applyCumulative(payments);

      console.log('Chart data extracted:');
      console.log('Labels:', labels);
      console.log('Original Transactions:', transactions);
      console.log('Original Interest:', interest);
      console.log('Original Payments:', payments);
      if (cumulativeEnabled) {
        console.log('Cumulative Transactions:', transactionsCumulative);
        console.log('Cumulative Interest:', interestCumulative);
        console.log('Cumulative Payments:', paymentsCumulative);
      }
      console.log('Beg Balance:', beg_balance);
      console.log('Net enabled:', netEnabled);
      console.log('Cumulative enabled:', cumulativeEnabled);

      // Clear previous chart if any
      if (chartRef.current) {
        console.log('Destroying previous chart');
        chartRef.current.destroy();
        chartRef.current = null;
      }

      let datasets;

      if (netEnabled) {
        console.log('Creating NET MODE datasets');
        
        // Calculate net data using cumulative values if enabled
        const netData = sortedData.map((item, index) => {
          const trans = cumulativeEnabled ? transactionsCumulative[index] : transactions[index];
          const int = cumulativeEnabled ? interestCumulative[index] : interest[index];
          const pay = cumulativeEnabled ? paymentsCumulative[index] : payments[index];
          const net = trans + int + pay;
          console.log(`Net calculation for ${labels[index]}: ${trans} + ${int} + ${pay} = ${net}`);
          return net;
        });

        console.log('Net data array:', netData);

        datasets = [
          // Hide balance fields in cumulative mode as they don't make sense
          ...(!cumulativeEnabled ? [{ 
            label: 'Beginning Balance', 
            data: beg_balance, 
            backgroundColor: 'rgba(189, 195, 199, 0.7)', // Light gray for beginning balance
            hidden: !globalDatasetVisibility[0]
          }] : []),
          {
            label: cumulativeEnabled ? 'Cumulative Net (Trans. + Interest + Payments + Credits)' : 'Net (Trans. + Interest + Payments + Credits)',
            data: netData,
            backgroundColor: netData.map(value =>
              value < 0 ? 'rgba(39, 174, 96, 0.8)' : 'rgba(231, 76, 60, 0.8)'  // Bright green for negative (good), Bright red for positive (bad)
            ),
            hidden: false // Ensure Net dataset is always visible
          },
          // Hide balance fields in cumulative mode
          ...(!cumulativeEnabled ? [{
            label: 'Balance Subject to IR',
            data: balance_int,
            backgroundColor: 'rgba(46, 204, 113, 0.7)', // Emerald green - distinct from other colors
            hidden: !globalDatasetVisibility[4]
          },
          {
            label: 'End Balance',
            data: end_balance,
            backgroundColor: 'rgba(52, 73, 94, 0.7)', // Dark gray for end balance
            hidden: !globalDatasetVisibility[5]
          }] : []),
        ];
      } else {
        console.log('Creating NORMAL MODE datasets');
        // Normal mode: Show individual series with muted but distinctive colors
        datasets = [
          // Hide balance fields in cumulative mode
          ...(!cumulativeEnabled ? [{
            label: 'Beginning Balance',
            data: beg_balance,
            backgroundColor: 'rgba(189, 195, 199, 0.7)', // Light gray for beginning balance
            hidden: !globalDatasetVisibility[0]
          }] : []),
          {
            label: cumulativeEnabled ? 'Cumulative Transactions' : 'Transactions',
            data: cumulativeEnabled ? transactionsCumulative : transactions,
            backgroundColor: 'rgba(52, 152, 219, 0.6)', // Muted blue for transactions
            hidden: !globalDatasetVisibility[1]
          },
          {
            label: cumulativeEnabled ? 'Cumulative Interest' : 'Interest',
            data: cumulativeEnabled ? interestCumulative : interest,
            backgroundColor: 'rgba(243, 156, 18, 0.7)', // Muted orange for interest
            hidden: !globalDatasetVisibility[2]
          },
          {
            label: cumulativeEnabled ? 'Cumulative Payments + Credits' : 'Payments + Credits',
            data: cumulativeEnabled ? paymentsCumulative : payments,
            backgroundColor: 'rgba(142, 68, 173, 0.6)', // Muted plum/burgundy for payments
            hidden: !globalDatasetVisibility[3]
          },
          // Hide balance fields in cumulative mode
          ...(!cumulativeEnabled ? [{
            label: 'Balance Subject to IR',
            data: balance_int,
            backgroundColor: 'rgba(46, 204, 113, 0.6)', // Muted purple
            hidden: !globalDatasetVisibility[4]
          },
          {
            label: 'End Balance',
            data: end_balance,
            backgroundColor: 'rgba(52, 73, 94, 0.7)', // Dark gray for end balance
            hidden: !globalDatasetVisibility[5]
          }] : []),
        ];
      }

      console.log('Final datasets:', datasets);
      console.log('Creating new Chart.js instance...');

      try {
        chartRef.current = new Chart(canvasRef.current, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: datasets,
          },
          options: {
            responsive: true,
            plugins: {
              legend: {
                labels: { color: '#e5e7eb' },
                onClick: (e, legendItem, legend) => {
                  const index = legendItem.datasetIndex;
                  const chart = legend.chart;
                  const meta = chart.getDatasetMeta(index);
                  
                  // Don't allow hiding the net bar through legend - it should be controlled by the button
                  if (chart.data.datasets[index].label?.includes('Net')) {
                    return; // Do nothing for net dataset
                  }
                  
                  // Calculate new visibility state
                  const isCurrentlyVisible = meta.hidden === null ? !chart.data.datasets[index].hidden : !meta.hidden;
                  const newVisibility = !isCurrentlyVisible;
                  
                  // Update the chart's meta immediately
                  meta.hidden = !newVisibility;
                  
                  // Update global state to persist across card switches
                  // Map the current dataset index to the original indices based on mode
                  let originalIndex = index;
                  
                  if (netEnabled) {
                    // In net mode, we have different dataset structure
                    if (cumulativeEnabled) {
                      // In net + cumulative mode: [Net]
                      // Net is dataset 0 but shouldn't be clickable (handled above)
                    } else {
                      // In net mode: [Beginning Balance, Net, Balance Subject to IR, End Balance]
                      // Map: 0->0, 1->net (skip), 2->4, 3->5
                      if (index === 0) originalIndex = 0; // Beginning Balance
                      else if (index === 2) originalIndex = 4; // Balance Subject to IR
                      else if (index === 3) originalIndex = 5; // End Balance
                    }
                  } else {
                    // In normal mode
                    if (cumulativeEnabled) {
                      // In cumulative mode: [Transactions, Interest, Payments]
                      // Map: 0->1, 1->2, 2->3
                      originalIndex = index + 1;
                    } else {
                      // In normal mode: [Beginning Balance, Transactions, Interest, Payments, Balance Subject to IR, End Balance]
                      // Direct mapping
                      originalIndex = index;
                    }
                  }
                  
                  if (originalIndex < 6) { // Only update for the original 6 datasets
                    globalDatasetVisibility[originalIndex] = newVisibility;
                  }
                  
                  // Trigger re-render to update averages
                  setVisibilityCounter(prev => prev + 1);
                  
                  chart.update();
                },
              },
            },
            scales: {
              x: {
                stacked: false,
                ticks: { color: '#e5e7eb' },
                grid: { color: '#374151' }
              },
              y: {
                beginAtZero: true,
                stacked: false,
                ticks: { color: '#e5e7eb' },
                grid: { color: '#374151' }
              },
            },
          },
        });
        
        console.log('Chart created successfully:', chartRef.current);
      } catch (error) {
        console.error('Error creating chart:', error);
      }
    };

    buildChart();
  }, [filteredData, netEnabled, cumulativeEnabled]); // Add cumulativeEnabled to dependencies

  // Handlers for predefined range buttons
  const handleRangeChange = (newRange) => {
    if (!data || data.length === 0) return;
    
    setActiveRange(newRange);
    globalActiveRange = newRange;
    try { window.localStorage.setItem('chart.activeRange', String(newRange)); } catch (e) {}

    // Filter out extreme future dates to avoid skewing "latest" relative ranges
    const validData = getFilteredDataForRange(data);
    if (validData.length === 0) return;

    const sortedData = [...validData].sort((a, b) => {
      return parseItemDate(a) - parseItemDate(b);
    });
    
    const latestDataPoint = sortedData[sortedData.length - 1];
    let latestDate = parseItemDate(latestDataPoint);
    
    // Cap latestDate at today if it's in the future
    const now = new Date();
    if (latestDate > now) {
      latestDate = now;
    }

    let startDate;

    if (newRange === 'YTD') {
      startDate = new Date(now.getFullYear(), 0, 1);
    } else if (newRange === '12months') {
      startDate = new Date(latestDate);
      startDate.setMonth(startDate.getMonth() - 11); // Go back 11 months to get 12 total
      startDate.setDate(1); // Start from beginning of that month
    } else if (newRange === '3months') {
      startDate = new Date(latestDate);
      startDate.setMonth(startDate.getMonth() - 2); // Go back 2 months to get 3 total
      startDate.setDate(1); // Start from beginning of that month
    }

    // End of range should be the last day of the current year for YTD per requirements
    const ytdEnd = new Date(now.getFullYear(), 11, 31);
    const endDate = (newRange === 'YTD') ? ytdEnd : latestDate;

    const newDateRange = { start: startDate, end: endDate };
    setDateRange(newDateRange);
    globalDateRange = newDateRange;
  };

  const handleShowAll = () => {
    setActiveRange('all');
    globalActiveRange = 'all';
    try { window.localStorage.setItem('chart.activeRange', 'all'); } catch (e) {}
    setDateRange({ start: null, end: null });
    globalDateRange = { start: null, end: null };
  };

  // Handle net toggle
  const handleNetToggle = () => {
    const newNetEnabled = !netEnabled;
    console.log('Net toggle clicked, current:', netEnabled, 'new:', newNetEnabled);
    
    setNetEnabled(newNetEnabled);
    globalNetEnabled = newNetEnabled;
    try { window.localStorage.setItem('chart.netEnabled', String(newNetEnabled)); } catch (e) {}
    
    console.log('Net state updated to:', newNetEnabled);
  };

  // Handle cumulative toggle
  const handleCumulativeToggle = () => {
    const newCumulativeEnabled = !cumulativeEnabled;
    console.log('Cumulative toggle clicked, current:', cumulativeEnabled, 'new:', newCumulativeEnabled);
    
    setCumulativeEnabled(newCumulativeEnabled);
    globalCumulativeEnabled = newCumulativeEnabled;
    try { window.localStorage.setItem('chart.cumulativeEnabled', String(newCumulativeEnabled)); } catch (e) {}
    
    console.log('Cumulative state updated to:', newCumulativeEnabled);
  };

  // Button styling helper
  const getButtonStyle = (rangeKey) => ({
    backgroundColor: activeRange === rangeKey ? '#007bff' : '#f8f9fa',
    color: activeRange === rangeKey ? 'white' : '#6c757d',
    border: '1px solid #dee2e6',
    padding: '0.375rem 0.75rem',
    margin: '0 0.25rem',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.875rem'
  });

  const getNetButtonStyle = () => ({
    backgroundColor: netEnabled ? '#28a745' : '#f8f9fa',
    color: netEnabled ? 'white' : '#6c757d',
    border: `1px solid ${netEnabled ? '#28a745' : '#dee2e6'}`,
    padding: '0.375rem 0.75rem',
    margin: '0 0.25rem',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  });

  const getCumulativeButtonStyle = () => ({
    backgroundColor: cumulativeEnabled ? '#17a2b8' : '#f8f9fa',
    color: cumulativeEnabled ? 'white' : '#6c757d',
    border: `1px solid ${cumulativeEnabled ? '#17a2b8' : '#dee2e6'}`,
    padding: '0.375rem 0.75rem',
    margin: '0 0.25rem',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  });

  // Calculate averages for visible metrics
  const calculateAverages = () => {
    if (!filteredData || filteredData.length === 0) return [];

    const extractMoneyValue = (item, fieldName) => {
      if (item[fieldName] !== undefined) {
        if (typeof item[fieldName] === 'number') return item[fieldName];
        if (item[fieldName] && typeof item[fieldName] === 'object' && 'amount' in item[fieldName]) {
          return Number(item[fieldName].amount) || 0;
        }
        if (typeof item[fieldName] === 'string') return Number(item[fieldName]) || 0;
      }
      return 0;
    };

    const metrics = [
      { id: 0, label: 'Beginning Balance', field: 'previous_balance' },
      { id: 1, label: 'Transactions', field: 'transactions' },
      { id: 2, label: 'Interest', field: 'interest_charged' },
      { id: 3, label: 'Payments + Credits', isPayment: true },
      { id: 4, label: 'Balance Subject to IR', field: 'balance_subject_to_interest_rate' },
      { id: 5, label: 'End Balance', field: 'new_balance' },
    ];

    return metrics.filter(m => globalDatasetVisibility[m.id]).map(m => {
      const values = filteredData.map(item => {
        if (m.isPayment) {
          const p = extractMoneyValue(item, 'payments');
          const oc = extractMoneyValue(item, 'other_credits');
          return p + oc;
        }
        return extractMoneyValue(item, m.field);
      });
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      return { label: `Avg. ${m.label}`, value: avg };
    });
  };

  const averages = calculateAverages();

  return (
    <div>
      {/* Dynamic Metrics Averages */}
      {averages.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '1.5em', padding: '15px', backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151' }}>
          {averages.map((avg, index) => (
            <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: '0.85rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{avg.label}</span>
              <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#e5e7eb' }}>
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(avg.value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/*/!* Debug info in development *!/*/}
      {/*{process.env.NODE_ENV === 'development' && (*/}
      {/*  <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>*/}
      {/*    Debug: netEnabled={netEnabled.toString()}, cumulativeEnabled={cumulativeEnabled.toString()}, data length={data?.length || 0}*/}
      {/*    {data && data.length > 0 && (*/}
      {/*      <div>First item keys: {Object.keys(data[0]).join(', ')}</div>*/}
      {/*    )}*/}
      {/*  </div>*/}
      {/*)}*/}

      {/* Range Buttons and Toggle Buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
        <div>
          <button 
            onClick={() => handleRangeChange('12months')}
            style={getButtonStyle('12months')}
          >
            Last 12 Months
          </button>
          <button 
            onClick={() => handleRangeChange('3months')}
            style={getButtonStyle('3months')}
          >
            Last 3 Months
          </button>
          <button 
            onClick={() => handleRangeChange('YTD')}
            style={getButtonStyle('YTD')}
          >
            YTD
          </button>
          <button
            onClick={handleShowAll}
            style={getButtonStyle('all')}
          >
            Show All
          </button>
        </div>
        
        {/* Toggle Buttons in top right */}
        <div>
          <button
            onClick={handleCumulativeToggle}
            style={getCumulativeButtonStyle()}
            title="Show cumulative values over time for flows (Transactions, Interest, Payments + Credits). Hides balance fields as they don't make sense cumulatively."
          >
            {cumulativeEnabled ? '✓ ' : ''}Cumulative
          </button>
          <button
            onClick={handleNetToggle}
            style={getNetButtonStyle()}
            title="Toggle between individual series (Transactions, Interest, Payments + Credits) and combined net view"
          >
            {netEnabled ? '✓ ' : ''}Net Trans., Interest, and Payments + Credits
          </button>
        </div>
      </div>

      {/* Chart container */}
      <canvas ref={canvasRef} />
    </div>
  );
}

export default GroupedBarChart;